import { useState, useEffect } from "react";
import { Trash2, Plus, Keyboard } from "lucide-react";
import { MODULES } from "@/lib/modules";
import { loadShortcuts, saveShortcuts, normalizeCombo, comboToLabel, buildActionList } from "@/lib/shortcuts";

export function ShortcutSettings({ t }) {
  const [list, setList] = useState(loadShortcuts());
  const [combo, setCombo] = useState("");
  const [action, setAction] = useState("panel:settings");
  const actions = buildActionList(MODULES);

  useEffect(() => {
    const r = () => setList(loadShortcuts());
    window.addEventListener("fp-shortcuts-changed", r);
    return () => window.removeEventListener("fp-shortcuts-changed", r);
  }, []);

  const onCapture = (e) => {
    e.preventDefault();
    const k = (e.key || "").toLowerCase();
    if (["control", "alt", "shift", "meta"].includes(k)) return; // wait for a real key
    setCombo(normalizeCombo(e));
  };

  const add = () => {
    if (!combo) return;
    const next = [...list.filter((s) => s.combo !== combo), { combo, action }];
    setList(next); saveShortcuts(next); setCombo("");
  };
  const remove = (c) => { const next = list.filter((s) => s.combo !== c); setList(next); saveShortcuts(next); };
  const labelOf = (id) => (actions.find((a) => a[0] === id)?.[1]) || id;

  const input = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500";

  return (
    <div data-testid="shortcut-settings">
      <p className="text-[11px] text-slate-400 mb-3">
        {t("Twórz własne skróty klawiszowe i przypisuj im funkcje. Skróty działają, gdy nie piszesz w polu tekstowym. Zapisywane są w tej przeglądarce.")}
      </p>

      <div className="rounded border border-slate-700 bg-slate-900/60 p-2 mb-3 text-[11px] text-slate-400" data-testid="shortcut-defaults">
        <div className="flex items-center gap-1.5 text-slate-300 mb-1"><Keyboard size={13} className="text-cyan-400" /> {t("Skróty wbudowane")}</div>
        <div><kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-600 text-slate-200">Esc</kbd> — {t("zamknij otwarty panel")} · <kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-600 text-slate-200">F</kbd> — {t("pełny ekran panelu")}</div>
      </div>

      <div className="space-y-1.5 mb-3" data-testid="shortcut-list">
        {list.length === 0 && <div className="text-xs text-slate-500">{t("Brak własnych skrótów.")}</div>}
        {list.map((s) => (
          <div key={s.combo} data-testid={`shortcut-row-${s.combo}`} className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5">
            <kbd className="px-2 py-0.5 rounded bg-slate-800 border border-slate-600 text-cyan-200 text-xs shrink-0">{comboToLabel(s.combo)}</kbd>
            <span className="text-sm text-slate-300 flex-1 truncate">{labelOf(s.action)}</span>
            <button onClick={() => remove(s.combo)} data-testid={`shortcut-remove-${s.combo}`} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Dodaj nowy skrót")}</label>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
        <input readOnly value={combo ? comboToLabel(combo) : ""} onKeyDown={onCapture}
          placeholder={t("Kliknij i naciśnij kombinację…")} data-testid="shortcut-capture" className={`${input} cursor-pointer`} />
        <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="shortcut-action" className={input}>
          {actions.map(([id, lbl]) => <option key={id} value={id}>{t(lbl)}</option>)}
        </select>
        <button onClick={add} data-testid="shortcut-add" className="shrink-0 bg-blue-600 hover:bg-blue-500 rounded px-3 py-2 text-sm flex items-center justify-center gap-1"><Plus size={15} /> {t("Dodaj")}</button>
      </div>
    </div>
  );
}
